import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { PUBLIC_URL } from "../../const";
import { socket } from "../../socket";
import LanguageSwitcher from "../core/switch-language";

const dict = {
    en: {
        title: "Complete Your Invitation",
        subtitle: "Add your personal details to finish onboarding",
        invitedTo: "You were invited to the workspace",
        invitedBy: "Invited by",
        firstName: "First Name",
        lastName: "Last Name",
        password: "Password",
        confirmPassword: "Confirm Password",
        submit: "Activate Account",
        ready: "Invitation accepted! You can now sign in.",
        successRedirect: "You successfully joined {name}. Redirecting to login in 3 seconds…",
        inviteReady: "Hello {name}, you already have access. Your login is {email}. Please use your existing credentials to sign in.",
        missingFields: "Please fill in all fields.",
        mismatch: "Passwords do not match.",
        inviteToken: "Invitation Token",
        infoLoading: "Loading invitation details…",
        infoError: "Unable to load invitation. Please request a new link.",
        backToLogin: "Back to Login",
        existingUserCTA: "Activate Workspace",
        existingUserProcessing: "Activating...",
        inviteExistingPrompt: "Hello {name}, you already have a profile. Click “Activate Workspace” to finish linking it.",
    },
    ru: {
        title: "Завершите приглашение",
        subtitle: "Добавьте свои данные, чтобы завершить регистрацию",
        invitedTo: "Вы приглашены в рабочую область",
        invitedBy: "Кто пригласил",
        firstName: "Имя",
        lastName: "Фамилия",
        password: "Пароль",
        confirmPassword: "Подтвердите пароль",
        submit: "Активировать аккаунт",
        ready: "Приглашение подтверждено! Теперь можно войти.",
        successRedirect: "Вы успешно зарегистрировались в компании {name}. Перенаправление на вход через 3 секунды…",
        inviteReady: "Добрый день, {name}. Вы уже успешно зарегистрировались. Ваш логин {email}. Просто войдите, используя свой аккаунт.",
        missingFields: "Заполните все поля.",
        mismatch: "Пароли не совпадают.",
        inviteToken: "Токен приглашения",
        infoLoading: "Загрузка данных приглашения…",
        infoError: "Не удалось загрузить приглашение. Запросите новую ссылку.",
        backToLogin: "Назад к входу",
        existingUserCTA: "Активировать workspace",
        existingUserProcessing: "Активируем...",
        inviteExistingPrompt: "Добрый день, {name}. У вас уже есть профиль. Нажмите «Активировать workspace», чтобы продолжить.",
    },
    ua: {
        title: "Завершіть запрошення",
        subtitle: "Додайте особисті дані, щоб завершити реєстрацію",
        invitedTo: "Вас запрошено до робочого простору",
        invitedBy: "Хто запросив",
        firstName: "Ім’я",
        lastName: "Прізвище",
        password: "Пароль",
        confirmPassword: "Підтвердіть пароль",
        submit: "Активувати акаунт",
        ready: "Запрошення підтверджено! Тепер можна увійти.",
        successRedirect: "Ви успішно приєдналися до {name}. Перенаправлення на вхід через 3 секунди…",
        inviteReady: "Вітаємо, {name}. Ви вже маєте доступ. Ваш логін {email}. Увійдіть, використовуючи свій обліковий запис.",
        missingFields: "Заповніть усі поля.",
        mismatch: "Паролі не збігаються.",
        inviteToken: "Токен запрошення",
        infoLoading: "Завантаження даних запрошення…",
        infoError: "Не вдалося завантажити запрошення. Запросіть нове посилання.",
        backToLogin: "Назад до входу",
        existingUserCTA: "Активувати workspace",
        existingUserProcessing: "Активуємо...",
        inviteExistingPrompt: "Вітаємо, {name}. У вас вже є профіль. Натисніть «Активувати workspace», щоб продовжити.",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const socketRequest = (event, responseEvent, payload = {}, timeoutMs = 15000) => {
    const requestId = `${event}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    return new Promise((resolve, reject) => {
        let settled = false;

        const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            socket.off(responseEvent, handler);
            reject(new Error("Request timed out"));
        }, timeoutMs);

        const handler = (response = {}) => {
            if (response.request_id !== requestId) {
                return;
            }
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            socket.off(responseEvent, handler);
            resolve(response);
        };

        socket.on(responseEvent, handler);
        socket.emit(event, { ...payload, request_id: requestId });
    });
};

const InviteAcceptForm = () => {
    const { token } = useParams();
    const [lang, setLang] = useState(getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const normalizeCompletedMessage = (message) =>
        typeof message === "string" && /already\s+(completed|accepted)/i.test(message);

    useEffect(() => {
        document.documentElement.lang = lang;
        localStorage.setItem("lang", lang);
    }, [lang]);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [inviteInfo, setInviteInfo] = useState(null);
    const [infoLoading, setInfoLoading] = useState(true);
    const [infoError, setInfoError] = useState("");
    const [existingUser, setExistingUser] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showReadyMessage, setShowReadyMessage] = useState(false);

    const assignInviteDetails = useCallback((normalized = {}) => {
        setInviteInfo(normalized);
        if (normalized.first_name) {
            setFirstName(normalized.first_name);
        }
        if (normalized.last_name) {
            setLastName(normalized.last_name);
        }
    }, []);

    const readyMessage = useMemo(() => {
        const name =
            inviteInfo?.first_name ||
            inviteInfo?.invitee_name ||
            inviteInfo?.name ||
            inviteInfo?.full_name ||
            firstName ||
            "";
        const email =
            inviteInfo?.invitee_email ||
            inviteInfo?.email ||
            inviteInfo?.recipient_email ||
            inviteInfo?.user_email ||
            inviteInfo?.login ||
            "";
        return (t.inviteReady || "")
            .replace("{name}", name || "")
            .replace("{email}", email || "");
    }, [inviteInfo, firstName, t.inviteReady]);

    const existingPrompt = useMemo(() => {
        const name =
            inviteInfo?.first_name ||
            inviteInfo?.invitee_name ||
            inviteInfo?.name ||
            inviteInfo?.full_name ||
            firstName ||
            "";
        return (t.inviteExistingPrompt || "")
            .replace("{name}", name || "");
    }, [inviteInfo, firstName, t.inviteExistingPrompt]);

    useEffect(() => {
        let cancelled = false;

        if (!token) {
            setInfoError(t.infoError);
            setInfoLoading(false);
            return;
        }

        const loadInviteInfo = async () => {
            setInfoLoading(true);
            setInfoError("");
            try {
                const response = await socketRequest(
                    "invitation_accept:info",
                    "invitation_accept:info-response",
                    { code: token, lang }
                );
                if (cancelled) return;

                const normalized = response?.data || null;
                const message = response?.err?.message || response?.message || t.infoError;

                if (response?.status === 1) {
                    if (normalized) {
                        assignInviteDetails(normalized);
                    } else {
                        setInviteInfo(null);
                    }
                    const alreadyRegistered =
                        typeof normalized?.already_registered === "boolean" ? normalized?.already_registered : false;
                    const statusValue = (normalized?.status || "").toString().toLowerCase();
                    const alreadyCompleted =
                        normalized?.already_completed === true ||
                        normalized?.invite_completed === true ||
                        ["accepted", "completed"].includes(statusValue);
                    setExistingUser(alreadyRegistered || alreadyCompleted);
                    setShowReadyMessage(false);
                    setInfoError("");
                } else {
                    if (normalizeCompletedMessage(message)) {
                        setExistingUser(true);
                        setShowReadyMessage(false);
                        if (normalized) {
                            assignInviteDetails(normalized);
                        } else {
                            setInviteInfo((prev) => prev || {});
                        }
                        setInfoError("");
                    } else {
                        if (normalized) {
                            assignInviteDetails(normalized);
                        } else {
                        setInviteInfo(null);
                        }
                        setInfoError(message);
                    }
                }
                setInfoLoading(false);
            } catch (err) {
                if (cancelled) return;
                setInviteInfo(null);
                setInfoError(err?.message || t.infoError);
                setInfoLoading(false);
            }
        };

        loadInviteInfo();
        return () => {
            cancelled = true;
        };
    }, [token, lang, t.infoError, assignInviteDetails]);

    const handleSubmit = async (event) => {
        if (event && typeof event.preventDefault === "function") {
            event.preventDefault();
        }
        setError("");
        setSuccess("");

        if (!existingUser) {
            if (!firstName || !lastName || !password || !confirmPassword) {
                setError(t.missingFields);
                return;
            }
            if (password !== confirmPassword) {
                setError(t.mismatch);
                return;
            }
        }

        if (!token) {
            setError(t.infoError);
            return;
        }

        setSubmitLoading(true);
        try {
            const payload = { code: token, lang };
            if (!existingUser) {
                payload.first_name = firstName;
                payload.last_name = lastName;
                payload.password = password;
                payload.confirm_password = confirmPassword;
            }

            const response = await socketRequest(
                "invitation_accept:complete",
                "invitation_accept:complete-response",
                payload
            );

            if (response?.status === 1) {
                const successMessage = response?.message || response?.data?.message || t.ready;
                setSuccess(successMessage);
                if (existingUser) {
                    setShowReadyMessage(true);
                }
                setTimeout(() => {
                    window.location.href = "/sign-in";
                }, 3000);
            } else {
                const message = response?.err?.message || t.infoError;
                if (normalizeCompletedMessage(message)) {
                    setExistingUser(true);
                    if (response?.data) {
                        assignInviteDetails(response.data);
                    }
                    setError("");
                    setShowReadyMessage(true);
                } else {
                    setError(message);
                }
            }
        } catch (err) {
            setError(err?.message || t.infoError);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1 fs-4 fw-semibold">{t.title}</h2>
                    <p className="text-muted mb-0">{t.subtitle}</p>
                </div>
                <LanguageSwitcher lang={lang} setLang={setLang} />
            </div>

            {!success && (
                <div className="mb-4 p-3 bg-primary-subtle rounded-3">
                    <div className="mb-1 text-primary fw-bold text-uppercase small">{t.invitedTo}</div>
                    <div className="h5 fw-semibold mb-3 text-dark">{inviteInfo?.workspace_name || "Demo Workspace Inc."}</div>
                    <div className="mb-1 text-primary fw-bold text-uppercase small">{t.invitedBy}</div>
                    <div className="h6 mb-0 text-dark">{inviteInfo?.invited_by_name || inviteInfo?.invited_by_email || "John Doe"}</div>
                </div>
            )}

            {success ? (
                <div className="alert alert-success">
                    {showReadyMessage
                        ? readyMessage
                        : t.successRedirect?.replace?.("{name}", inviteInfo?.workspace_name || "") || t.ready}
                </div>
            ) : (
                <>
                    {infoLoading && <div className="alert alert-light text-muted">{t.infoLoading}</div>}
                    {infoError && <div className="alert alert-danger">{infoError}</div>}
                    {existingUser && !success && (
                        <div className="alert alert-info">{existingPrompt}</div>
                    )}
                    {error && <div className="alert alert-danger">{error}</div>}
                    {existingUser && !success && (
                        <button
                            type="button"
                            className="btn btn-primary w-100 py-3 mb-3"
                            onClick={handleSubmit}
                            disabled={submitLoading}
                        >
                            {submitLoading ? t.existingUserProcessing : t.existingUserCTA}
                        </button>
                    )}
                </>
            )}

            {!existingUser && !success && (
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">{t.firstName}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={firstName}
                            onChange={(event) => setFirstName(event.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">{t.lastName}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={lastName}
                            onChange={(event) => setLastName(event.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">{t.password}</label>
                        <input
                            type="password"
                            className="form-control"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-semibold">{t.confirmPassword}</label>
                        <input
                            type="password"
                            className="form-control"
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                        />
                    </div>

                    <button type="submit" className="btn btn-primary w-100 py-3 mt-4" disabled={submitLoading}>
                        {t.submit}
                    </button>
                </form>
            )}

            <a href="/sign-in/" className="btn bg-primary-subtle text-primary w-100 mt-3">
                {t.backToLogin}
            </a>
        </div>
    );
};

const InviteAcceptPage = () => {
    return (
        <div id="main-wrapper">
            <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100">
                <div className="position-relative z-index-5">
                    <div className="row">
                        <div className="col-xl-7 col-xxl-8">
                                    <a href="https://my-sites-guard.com/" className="text-nowrap logo-img d-block px-4 py-9 w-100">
                                        <img
                                            src={`https://my-sites-guard.com/assets/img/home_logo.png`}
                                    className="dark-logo"
                                    alt="Logo-Dark"
                                    style={{ maxHeight: "40px" }}
                                />
                            </a>
                            <div className="d-none d-xl-flex align-items-center justify-content-center h-n80">
                                <img
                                    src={`${PUBLIC_URL}/assets/images/backgrounds/login-bg.png`}
                                    alt=""
                                    className="img-fluid"
                                    width="500"
                                />
                            </div>
                        </div>
                        <div className="col-xl-5 col-xxl-4">
                            <div className="authentication-login min-vh-100 bg-body row justify-content-center align-items-center p-4">
                                <div className="auth-max-width col-sm-8 col-md-6 col-xl-7 px-4">
                                    <InviteAcceptForm />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteAcceptPage;
